import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, useTheme } from '@material-ui/core';
import CommonButton from '../../../common/CommonButton';
import CommonDivider from '../../../common/CommonDivider';
import withStyles from '@material-ui/core/styles/withStyles';
import CommonMultiSelectField from '../../../common/form/CommonMultiSelectField';
import PropTypes from 'prop-types';
import {
  STANDARD_INPUT_HEIGHT,
  STANDARD_INPUT_RADIUS,
  STANDARD_INPUT_WIDTH,
  STANDARD_MOBILE_INPUT_HEIGHT,
} from '../../../../const/chart-const';
import { lighten } from '@material-ui/core/styles';
import {
  BACKGROUND_WHITE,
  BLACK_COLOR, DARK_BORDER_COLOR,
  GREY_COLOR,
  LIGHT_GREY_COLOR,
} from '../../../../const/color-const';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { fetchStandardReports } from '../../../../actions/report-actions';
import { areValuesOutOfDateRange, renderStandardReportFormFields } from '../standard/StandardReportUtils';
import LoadingSnackbar from '../../../common/LoadingSnackbar';
import { DATE_OPTIONS_STANDARD_REPORT } from '../../../../utils/dates-const';
import CommonSelectField from '../../../common/form/CommonSelectField';

const styles = theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    '& .MultiSelectField': {
      height: 56,
      '& div[class*="selectItem"]': {
        borderRadius: STANDARD_INPUT_RADIUS,
      },
    },
    '& .commonSelectField': {
      '& div[class*="selectField"]': {
        borderRadius: STANDARD_INPUT_RADIUS,
      },
    },
  },
  datePickersWrapper: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '& p.Mui-error': {
      position: 'absolute',
      bottom: -12,
      left: 10,
    },
  },
  loadingBar: {
    marginTop: theme.spacing(2),
  },
  fieldsWrapper: {
    '& .dateTimePickerWrapper': {
      '&:hover': {
        border: theme.palette.type === 'light' ? `1px solid ${BLACK_COLOR}` : `1px solid white`,
      },
    },
    '& > div': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      height: STANDARD_MOBILE_INPUT_HEIGHT,
      marginTop: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      border: theme.palette.type === 'light' ? `1px solid ${LIGHT_GREY_COLOR}` : `1px solid ${DARK_BORDER_COLOR}`,
      '&:hover': {
        '& fieldset': {
          border: theme.palette.type === 'light' ? `1px solid ${BLACK_COLOR}` : `1px solid white`,
        },
      },
      [theme.breakpoints.up('md')]: {
        height: STANDARD_INPUT_HEIGHT,
        width: STANDARD_INPUT_WIDTH,
        margin: theme.spacing(1),
      },
      '& label': {
        border: 'none',
        marginLeft: 10,
      },
      '& > div': {
        width: '100%',
        '& input': {
          marginLeft: 10,
          padding: 0,
          fontSize: 14,
          [theme.breakpoints.up('md')]: {
            fontSize: 16,
          },
        },
      },
      '& div': {
        '&::after': {
          display: 'none',
        },
        '&::before': {
          display: 'none',
        },
      },
    },
    '& .commonSelectField': {
      margin: theme.spacing(1, 0, 1, 0),
      [theme.breakpoints.up('md')]: {
        margin: theme.spacing(1),
      },
      border: 'none',
      '& label': {
        marginLeft: 0,
      },
    },
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
});

const SalesOverviewReportForm = (props) => {
  const { classes, machines, fetchStandardReports, reportName, fields, setFormValues } = props;
  const [loadingProgress, setLoadingProgress] = useState();
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const removeDateRange = ({ dateRange, ...rest }) => rest;
  const theme = useTheme();
  
  const dateRangeFieldStyle = (values) => {
    return {
      color: areValuesOutOfDateRange(values)
        ? theme.palette.type === 'light'
          ? LIGHT_GREY_COLOR
          : GREY_COLOR
        : 'unset',
    };
  };
  
  const getInitialValues = (fields) => {
    return {
      ...Object.fromEntries(fields.map(value => [value.name, value.initialValue ?? value.initialValue])),
      machineIDs: [null],
    };
  };
  
  return (
    <>
      <Formik
        initialValues={getInitialValues(fields)}
        onSubmit={(values, formikHelpers) => {
          setFormValues(values);
          fetchStandardReports(reportName, removeDateRange(values), formikHelpers, setLoadingProgress);
        }}>
        {(form) => {
          const { isSubmitting, values, submitForm, isValid } = form;
          return (
            <Form className={classes.form}>
              <Field
                wide
                atLeastOneElement
                name="machineIDs"
                label={
                  <FormattedMessage
                    id="field.cash.register"
                    defaultMessage="Cash Register"
                  />}
                options={[...machinesOptions, {
                  label: <>- <FormattedMessage
                    id="all"
                    defaultMessage="All"
                  /> -</>, value: null,
                }]}
                component={CommonMultiSelectField}
              />
              
              <div className={classes.fieldsWrapper}>
                {renderStandardReportFormFields(fields, form)}
                
                <Field
                  date
                  name="dateRange"
                  label={<FormattedMessage id="date.field.date.range" defaultMessage="Date range" />}
                  options={DATE_OPTIONS_STANDARD_REPORT}
                  component={CommonSelectField}
                  style={dateRangeFieldStyle(values)}
                />
              </div>
              
              <CommonButton
                isAnimated
                onClick={() => submitForm()}
                size="large"
                label={<FormattedMessage
                  id="search"
                  defaultMessage="Search"
                />}
                disabled={isSubmitting || !values.machineIDs || !values.dateFrom || !values.dateTo || !isValid}
              />
              
              {isSubmitting && <div className={classes.loadingBar}>
                <CircularProgress />
              </div>}
              <CommonDivider />
            </Form>
          );
        }}
      </Formik>
      
      <LoadingSnackbar
        progress={loadingProgress}
        label={<FormattedMessage id="loading" defaultMessage="Loading" />}
      />
    </>
  );
};

SalesOverviewReportForm.propTypes = {
  classes: PropTypes.object,
  machines: PropTypes.array,
  fetchStandardReports: PropTypes.func,
  reportName: PropTypes.string,
  fields: PropTypes.array,
  setFormValues: PropTypes.func,
};

const decorate = compose(
  withStyles(styles),
  connect(store => ({
      machines: store.user.machines,
      salesStartTime: store.user.salesStartTime,
    }),
    dispatch => bindActionCreators({ fetchStandardReports }, dispatch)),
);

export default decorate(SalesOverviewReportForm);
